import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Plus, X, Check, GitBranch, Circle, CheckCircle2, Archive, Trash2, Edit3, ChevronDown, ChevronUp } from "lucide-react";

function DecisionForm({ initial, onClose, onSave }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [options, setOptions] = useState(initial?.options || [{ option_id: "a", title: "Option A", description: "", pros: [], cons: [] }, { option_id: "b", title: "Option B", description: "", pros: [], cons: [] }]);
  const [dueDate, setDueDate] = useState(initial?.due_date?.slice(0,10) || "");

  const updateOpt = (i, field, val) => setOptions(opts => opts.map((o, idx) => idx===i?{...o,[field]:val}:o));
  const addOpt = () => setOptions(opts => [...opts, { option_id: String(Date.now()), title: `Option ${String.fromCharCode(65+opts.length)}`, description: "", pros: [], cons: [] }]);

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-lg shadow-2xl my-4" onClick={e=>e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-4">{initial?"Edit":"New"} decision</h3>
        <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Decision title..."
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none focus:border-[#5A90F5] mb-3"/>
        <textarea value={desc} onChange={e=>setDesc(e.target.value)} placeholder="What needs to be decided?"
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#A1A1AA] placeholder-[#3A3A3A] outline-none resize-none mb-4" rows={2}/>
        <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2">Options</p>
        {options.map((o,i) => (
          <div key={o.option_id} className="mb-3 p-3 bg-[#0A0A0A] rounded-xl border border-[#2A2A2A]">
            <div className="flex items-center gap-2 mb-2">
              <input value={o.title} onChange={e=>updateOpt(i,"title",e.target.value)} className="flex-1 bg-transparent text-[12px] font-semibold text-[#EDEDED] outline-none"/>
              {options.length > 2 && <button onClick={()=>setOptions(opts=>opts.filter((_,idx)=>idx!==i))} className="text-[#3A3A3A] hover:text-[#EF4444]"><X className="w-3 h-3"/></button>}
            </div>
            <input value={o.description} onChange={e=>updateOpt(i,"description",e.target.value)} placeholder="Describe this option..."
              className="w-full bg-transparent text-[11px] text-[#52525B] placeholder-[#3A3A3A] outline-none"/>
          </div>
        ))}
        <button onClick={addOpt} className="text-[11px] text-[#5A90F5] hover:text-[#4A80E5] mb-4">+ Add option</button>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B]">Cancel</button>
          <button onClick={()=>{if(title.trim())onSave({title:title.trim(),description:desc,options,due_date:dueDate||null})}} disabled={!title.trim()} className="flex-1 py-2 rounded-xl text-[12px] font-semibold bg-[#5A90F5] text-white disabled:opacity-30">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function ABBoard() {
  const { current } = useWorkspace();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [reasoning, setReasoning] = useState({});
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try { const { data } = await api.get(`/workspaces/${wsId}/decisions`); setItems(data); } catch {}
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try { const { data: d } = await api.post(`/workspaces/${wsId}/decisions`, data); setItems(i=>[d,...i]); setShowForm(false); } catch { toast.error("Failed"); }
  };
  const update = async (data) => {
    try { const { data: d } = await api.put(`/workspaces/${wsId}/decisions/${editing.decision_id}`, data); setItems(i=>i.map(x=>x.decision_id===d.decision_id?d:x)); setEditing(null); } catch {}
  };
  const choose = async (decId, optionId) => {
    try { const { data: d } = await api.put(`/workspaces/${wsId}/decisions/${decId}`, { chosen_option_id: optionId, status: "decided", reasoning: reasoning[decId] || "" }); setItems(i=>i.map(x=>x.decision_id===d.decision_id?d:x)); } catch {}
  };
  const del = async (id) => {
    if(!window.confirm("Delete?"))return;
    try { await api.delete(`/workspaces/${wsId}/decisions/${id}`); setItems(i=>i.filter(x=>x.decision_id!==id)); } catch {}
  };

  const filtered = items.filter(d => filter === "all" || d.status === filter);

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold text-[#EDEDED]">A/B</h1>
          <p className="text-[11px] text-[#3A3A3A] mt-0.5">{items.filter(d=>d.status==="open").length} open decisions</p>
        </div>
        <button onClick={()=>setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#5A90F5] hover:bg-[#4A80E5] text-white text-[12px] font-semibold">
          <Plus className="w-3.5 h-3.5"/> New
        </button>
      </div>
      <div className="px-8 py-3 border-b border-[#1A1A1A] flex gap-2">
        {["open","decided","archived","all"].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} className={`px-3 py-1 rounded-lg text-[10px] capitalize border ${filter===f?"border-[#5A90F5]/40 bg-[#5A90F5]/10 text-[#5A90F5]":"border-[#2A2A2A] text-[#3A3A3A] hover:text-[#52525B]"}`}>{f}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-4 space-y-3">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3"><GitBranch className="w-8 h-8 text-[#2A2A2A]"/><p className="text-[13px] text-[#3A3A3A]">No {filter==="all"?"":filter} decisions</p></div>
        ) : filtered.map(d => {
          const chosen = d.options?.find(o=>o.option_id===d.chosen_option_id);
          const isOpen = d.status==="open";
          const isExp = expanded[d.decision_id];
          return (
            <div key={d.decision_id} className="bg-[#111] border border-[#2A2A2A] rounded-2xl overflow-hidden">
              <div className="flex items-start gap-3 p-4 cursor-pointer" onClick={()=>setExpanded(e=>({...e,[d.decision_id]:!e[d.decision_id]}))}>
                <GitBranch className={`w-4 h-4 flex-shrink-0 mt-0.5 ${d.status==="decided"?"text-[#00C781]":d.status==="archived"?"text-[#3A3A3A]":"text-[#5A90F5]"}`}/>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#EDEDED]">{d.title}</p>
                  {d.description && <p className="text-[11px] text-[#52525B] mt-0.5 line-clamp-1">{d.description}</p>}
                  {chosen && <p className="text-[10px] text-[#00C781] mt-1">→ Chose: {chosen.title}</p>}
                  <p className="text-[10px] text-[#3A3A3A] mt-1">{d.options?.length||0} options · {new Date(d.created_at).toLocaleDateString([],{month:"short",day:"numeric"})}</p>
                </div>
                <div className="flex items-center gap-1">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full capitalize ${d.status==="decided"?"bg-[#00C781]/15 text-[#00C781]":d.status==="archived"?"bg-[#1A1A1A] text-[#3A3A3A]":"bg-[#5A90F5]/15 text-[#5A90F5]"}`}>{d.status}</span>
                  {isExp ? <ChevronUp className="w-3.5 h-3.5 text-[#3A3A3A]"/> : <ChevronDown className="w-3.5 h-3.5 text-[#3A3A3A]"/>}
                </div>
              </div>
              {isExp && (
                <div className="border-t border-[#1A1A1A] p-4">
                  <div className="grid gap-3" style={{gridTemplateColumns:`repeat(${Math.min(d.options?.length||1,3)},1fr)`}}>
                    {d.options?.map(o => {
                      const isChosen = o.option_id === d.chosen_option_id;
                      return (
                        <div key={o.option_id} className={`p-3 rounded-xl border transition-all ${isChosen?"border-[#00C781]/40 bg-[#00C781]/5":"border-[#2A2A2A] bg-[#0A0A0A]"}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[12px] font-semibold text-[#EDEDED]">{o.title}</p>
                            {isChosen && <CheckCircle2 className="w-3.5 h-3.5 text-[#00C781]"/>}
                          </div>
                          {o.description && <p className="text-[10px] text-[#52525B] mb-2">{o.description}</p>}
                          {isOpen && (
                            <button onClick={()=>choose(d.decision_id, o.option_id)}
                              className="w-full py-1.5 rounded-lg text-[10px] border border-[#3A3A3A] text-[#52525B] hover:border-[#00C781]/40 hover:text-[#00C781] hover:bg-[#00C781]/5 transition-all">
                              Choose this
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {isOpen && (
                    <div className="mt-3">
                      <input value={reasoning[d.decision_id]||""} onChange={e=>setReasoning(r=>({...r,[d.decision_id]:e.target.value}))} placeholder="Reasoning (optional)..."
                        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[11px] text-[#A1A1AA] placeholder-[#3A3A3A] outline-none"/>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button onClick={()=>setEditing(d)} className="p-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA]"><Edit3 className="w-3.5 h-3.5"/></button>
                    <button onClick={()=>del(d.decision_id)} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#3A3A3A] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showForm && <DecisionForm onClose={()=>setShowForm(false)} onSave={create}/>}
      {editing && <DecisionForm initial={editing} onClose={()=>setEditing(null)} onSave={update}/>}
    </div>
  );
}
